import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserCheck } from "lucide-react";
import { api } from "../api";
import { PasswordInput } from "../components/PasswordInput";
import { AuthLayout } from "../components/AuthLayout";

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [invite, setInvite] = useState<{ email: string; tenant_name: string; role: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("This invitation link is missing its token. Ask your admin to resend it.");
      setLoading(false);
      return;
    }
    api.peekInvite(token)
      .then((inv) => { setInvite(inv); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const passwordError =
    password.length === 0 ? null
    : password.length < 8 ? "Use at least 8 characters."
    : null;
  const confirmError =
    confirm.length === 0 ? null
    : confirm !== password ? "Passwords don't match."
    : null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (passwordError || confirmError || !password) {
      setError(passwordError || confirmError || "Fill both password fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.acceptInvite(token, password);
      navigate("/login?invited=1");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle={invite
        ? `Set a password to activate your ${invite.role} account on ${invite.tenant_name}.`
        : "Set a password to activate your account."}
    >
      {loading && (
        <div className="card"><div className="card-body text-center text-slate-500 text-sm">Loading invitation…</div></div>
      )}

      {!loading && error && !invite && (
        <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {!loading && invite && (
        <form onSubmit={onSubmit} className="card" noValidate>
          <div className="card-body space-y-4">
            <div className="flex items-start gap-3 text-sm bg-brand-50 border border-brand-100 rounded px-3 py-2">
              <UserCheck className="h-4 w-4 mt-0.5 text-brand-600 shrink-0" aria-hidden="true" />
              <div className="text-brand-800">
                Joining <strong>{invite.tenant_name}</strong> as <span className="capitalize">{invite.role}</span>.
              </div>
            </div>
            <div>
              <label htmlFor="ai-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input id="ai-email" className="input bg-slate-50 cursor-not-allowed" value={invite.email} disabled />
            </div>

            <PasswordInput
              id="ai-password"
              label="Choose a password"
              value={password}
              onChange={setPassword}
              autoFocus
              minLength={8}
              autoComplete="new-password"
              disabled={submitting}
              error={passwordError}
              helpText="At least 8 characters."
            />

            <PasswordInput
              id="ai-confirm"
              label="Confirm password"
              value={confirm}
              onChange={setConfirm}
              minLength={8}
              autoComplete="new-password"
              disabled={submitting}
              error={confirmError}
            />

            {error && (
              <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting || !password || !!passwordError || !!confirmError || password !== confirm}
            >
              {submitting ? (
                <><span className="spinner" aria-hidden="true" /> Setting up…</>
              ) : (
                "Accept and create account"
              )}
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
